import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { toast } from "sonner";
import { ScanLine, Camera, Trash2, Pause, PlayCircle, Minus, Plus, Lock } from "lucide-react";
import { useApp } from "@/lib/app-context";
import { inr, errMsg, uid, dt } from "@/lib/api";
import { Field, NativeSelect } from "@/components/common";
import { CameraScanner } from "@/components/CameraScanner";
import { PrintDialog } from "@/components/PrintDoc";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";

const COUNT = ["pcs", "pack", "box"];

function estimate(cart, billDisc, incl) {
  const gross = cart.reduce((s, c) => s + c.qty * c.price, 0);
  const lineDisc = cart.reduce((s, c) => s + Math.min(c.discount || 0, c.qty * c.price), 0);
  const net = gross - lineDisc;
  const bd = Math.min(billDisc || 0, net);
  let tax = 0, total = 0;
  cart.forEach((c) => {
    const n = c.qty * c.price - Math.min(c.discount || 0, c.qty * c.price);
    const share = net ? (bd * n) / net : 0;
    const v = n - share, r = (c.product.tax_rate || 0) / 100;
    if (incl) { tax += v - v / (1 + r); total += v; } else { tax += v * r; total += v * (1 + r); }
  });
  return { gross, discount: lineDisc + bd, tax, grand: Math.round(total) };
}

function ShiftBar({ shift, onOpen, onClose }) {
  if (!shift) return (
    <div className="flex items-center justify-between rounded-xl bg-amber-50 border border-amber-200 p-3 mb-4" data-testid="no-shift-banner">
      <span className="text-sm font-medium text-amber-900"><Lock className="inline h-4 w-4 mr-1" />Open a cashier shift to start billing</span>
      <Button size="sm" onClick={onOpen} data-testid="open-shift-button">Open shift</Button>
    </div>
  );
  return (
    <div className="flex flex-wrap items-center gap-x-6 gap-y-1 rounded-xl bg-slate-900 text-slate-100 px-4 py-2.5 mb-4 text-sm" data-testid="shift-bar">
      <span><b>{shift.cashier}</b> · {shift.counter}</span><span>Opened {dt(shift.opened_at)}</span><span>Opening cash {inr(shift.opening_cash)}</span>
      <Button size="sm" variant="secondary" className="ml-auto" onClick={onClose} data-testid="close-shift-button">Close shift</Button>
    </div>
  );
}

export default function POS() {
  const { api, branchId, settings, can } = useApp();
  const [shift, setShift] = useState(undefined);
  const [cats, setCats] = useState([]);
  const [cat, setCat] = useState("");
  const [products, setProducts] = useState([]);
  const [q, setQ] = useState("");
  const [cart, setCart] = useState([]);
  const [customer, setCustomer] = useState({ name: "Walk-in Customer", mobile: "" });
  const [ptype, setPtype] = useState("cash");
  const [split, setSplit] = useState({});
  const [tendered, setTendered] = useState("");
  const [ref, setRef] = useState("");
  const [billDisc, setBillDisc] = useState(0);
  const [busy, setBusy] = useState(false);
  const [cam, setCam] = useState(false);
  const [printDoc, setPrintDoc] = useState(null);
  const [held, setHeld] = useState([]);
  const [heldOpen, setHeldOpen] = useState(false);
  const [shiftDlg, setShiftDlg] = useState(null);
  const [cashInput, setCashInput] = useState("");
  const idem = useRef(uid());
  const searchRef = useRef(null);
  const methods = (settings?.payment_methods || []).filter((m) => m.enabled);

  const loadShift = useCallback(() => api.get("/shifts/current").then((r) => setShift(r.data)), [api]);
  useEffect(() => {
    loadShift();
    api.get("/categories").then((r) => setCats(r.data));
  }, [api, loadShift]);
  useEffect(() => {
    const t = setTimeout(() => api.get("/products", { params: { q, category_id: cat, status: "active", branch_id: branchId, limit: 60 } }).then((r) => setProducts(r.data)), 200);
    return () => clearTimeout(t);
  }, [q, cat, branchId, api]);

  const add = (p, qty = null) => {
    if (p.stock && p.track_stock !== false && p.stock.available <= 0) toast.warning(`${p.name}: no sellable stock in this branch`);
    setCart((c) => {
      const i = c.findIndex((x) => x.product.id === p.id);
      const step = qty ?? 1;
      if (i >= 0 && qty === null) return c.map((x, j) => (j === i ? { ...x, qty: +(x.qty + 1).toFixed(3) } : x));
      return [...c, { product: p, qty: step, price: p.effective_offline_price, discount: 0 }];
    });
  };
  const scan = async (code) => {
    if (!code.trim()) return;
    try {
      const r = await api.get("/products/lookup", { params: { code: code.trim(), branch_id: branchId } });
      add(r.data.product, r.data.qty);
      setQ("");
      if (r.data.source === "weighted") toast.success(`${r.data.product.name}: ${r.data.qty} ${r.data.product.unit}`);
    } catch (e) {
      if (e?.response?.status === 404 && products.length === 1) { add(products[0]); setQ(""); } else toast.error(errMsg(e));
    }
    searchRef.current?.focus();
  };
  const est = useMemo(() => estimate(cart, Number(billDisc), settings?.prices_include_tax !== false), [cart, billDisc, settings]);
  const upd = (i, patch) => setCart((c) => c.map((x, j) => (j === i ? { ...x, ...patch } : x)));

  const payments = () => {
    if (ptype === "split") return Object.entries(split).filter(([, v]) => Number(v) > 0).map(([method, amount]) => ({ method, amount: Number(amount), reference: method === "cash" ? "" : ref }));
    return [{ method: ptype, amount: ptype === "cash" ? Number(tendered || est.grand) : est.grand, reference: ref }];
  };
  const checkout = async () => {
    if (!cart.length || busy) return;
    setBusy(true);
    try {
      const r = await api.post("/pos/checkout", {
        idem_key: idem.current, branch_id: branchId, shift_id: shift?.id, customer, bill_discount: Number(billDisc || 0), payment_type: ptype, payments: payments(),
        items: cart.map((c) => ({ product_id: c.product.id, qty: Number(c.qty), discount: Number(c.discount || 0), price: Number(c.price) })),
      });
      toast.success(`Bill ${r.data.number} saved${r.data.change > 0 ? ` · Change ${inr(r.data.change)}` : ""}`);
      setPrintDoc(r.data);
      setCart([]); setSplit({}); setTendered(""); setRef(""); setBillDisc(0); setCustomer({ name: "Walk-in Customer", mobile: "" });
      idem.current = uid();
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setBusy(false);
    }
  };
  useEffect(() => {
    const h = (e) => { if (e.key === "F8") { e.preventDefault(); checkout(); } };
    window.addEventListener("keydown", h);
    return () => window.removeEventListener("keydown", h);
  });
  const hold = async () => {
    await api.post("/held-bills", { branch_id: branchId, cart: cart.map((c) => ({ product: c.product, qty: c.qty, discount: c.discount, price: c.price })), customer, note: "" });
    setCart([]); toast.success("Bill held");
  };
  const openHeld = async () => { setHeld((await api.get("/held-bills", { params: { branch_id: branchId } })).data); setHeldOpen(true); };
  const resume = async (h) => { setCart(h.cart); setCustomer(h.customer || customer); await api.delete(`/held-bills/${h.id}`); setHeldOpen(false); };
  const shiftAction = async () => {
    try {
      if (shiftDlg === "open") await api.post("/shifts/open", { branch_id: branchId, opening_cash: Number(cashInput || 0) });
      else { const r = await api.post(`/shifts/${shift.id}/close`, { counted_cash: Number(cashInput || 0) }); toast.success(`Shift closed. Expected ${inr(r.data.summary.expected_cash)}, variance ${inr(r.data.variance)}`); }
      setShiftDlg(null); setCashInput(""); loadShift();
    } catch (e) { toast.error(errMsg(e)); }
  };
  const [closeSummary, setCloseSummary] = useState(null);
  useEffect(() => {
    if (shiftDlg === "close") api.get("/shifts").then((r) => setCloseSummary(r.data.find((s) => s.id === shift?.id)?.summary));
  }, [shiftDlg, api, shift]);

  if (shift === undefined) return <div className="text-sm text-muted-foreground">Loading POS…</div>;
  return (
    <div>
      <ShiftBar shift={shift} onOpen={() => setShiftDlg("open")} onClose={() => setShiftDlg("close")} />
      <div className="grid grid-cols-12 gap-4 lg:h-[calc(100vh-10.5rem)]">
        <div className="col-span-12 lg:col-span-7 xl:col-span-8 flex flex-col min-h-0">
          <div className="flex gap-2">
            <div className="relative flex-1"><ScanLine className="absolute left-3 top-3 h-5 w-5 text-emerald-600" />
              <Input ref={searchRef} autoFocus data-testid="pos-search-input" className="h-12 pl-10 text-base" placeholder="Scan barcode or type name / SKU, press Enter"
                value={q} onChange={(e) => setQ(e.target.value)} onKeyDown={(e) => e.key === "Enter" && scan(q)} /></div>
            <Button variant="outline" className="h-12" onClick={() => setCam(true)} data-testid="pos-camera-button"><Camera className="h-5 w-5" /></Button>
          </div>
          <div className="flex gap-2 overflow-x-auto py-3">
            <Button size="sm" variant={cat === "" ? "default" : "outline"} onClick={() => setCat("")} data-testid="pos-category-all">All</Button>
            {cats.map((c) => <Button key={c.id} size="sm" variant={cat === c.id ? "default" : "outline"} onClick={() => setCat(c.id)} className="whitespace-nowrap" data-testid={`pos-category-${c.id}`}>{c.name}</Button>)}
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-3 xl:grid-cols-4 gap-3 overflow-y-auto pr-1 pb-2">
            {products.map((p) => (
              <button key={p.id} onClick={() => add(p)} data-testid={`pos-product-card-${p.erp_code}`} className="tile text-left rounded-xl border bg-white p-3 flex flex-col gap-1 min-h-[104px]">
                <span className="font-semibold text-sm leading-tight line-clamp-2">{p.name}</span>
                <span className="text-[11px] font-mono text-muted-foreground">{p.erp_code} · {p.stock.racks[0] || "no rack"}</span>
                <span className="mt-auto flex items-end justify-between"><span className="text-lg font-bold text-emerald-700 font-display">{inr(p.effective_offline_price)}</span>
                  <span className={`text-[11px] rounded px-1.5 py-0.5 ${p.stock.available > 0 ? "bg-slate-100" : "bg-red-100 text-red-700"}`}>{p.stock.available} {p.unit}</span></span>
              </button>))}
          </div>
        </div>
        <div className="col-span-12 lg:col-span-5 xl:col-span-4 bg-card border rounded-xl flex flex-col min-h-0 shadow-lg" data-testid="pos-cart">
          <div className="p-3 border-b grid grid-cols-2 gap-2">
            <Input data-testid="pos-customer-name" value={customer.name} onChange={(e) => setCustomer({ ...customer, name: e.target.value })} placeholder="Customer name" />
            <Input data-testid="pos-customer-mobile" value={customer.mobile} onChange={(e) => setCustomer({ ...customer, mobile: e.target.value.replace(/\D/g, "").slice(0, 10) })} placeholder="Mobile (optional)" />
          </div>
          <div className="flex-1 overflow-y-auto divide-y min-h-[160px]">
            {cart.length === 0 && <div className="p-8 text-center text-sm text-muted-foreground">Scan or tap products to add</div>}
            {cart.map((c, i) => {
              const loose = !COUNT.includes(c.product.unit);
              return (
                <div key={c.product.id} className="p-3" data-testid={`cart-line-${c.product.erp_code}`}>
                  <div className="flex justify-between gap-2"><span className="text-sm font-semibold">{c.product.name}</span>
                    <button onClick={() => setCart(cart.filter((_, j) => j !== i))} data-testid={`cart-remove-${c.product.erp_code}`}><Trash2 className="h-4 w-4 text-red-500" /></button></div>
                  <div className="flex items-center gap-2 mt-2">
                    {!loose && <Button size="icon" variant="outline" className="h-8 w-8" onClick={() => upd(i, { qty: Math.max(1, c.qty - 1) })}><Minus className="h-3 w-3" /></Button>}
                    <Input data-testid={`cart-qty-${c.product.erp_code}`} type="number" step={loose ? "0.001" : "1"} className="h-8 w-20 text-center" value={c.qty} onChange={(e) => upd(i, { qty: Number(e.target.value) })} />
                    {!loose && <Button size="icon" variant="outline" className="h-8 w-8" onClick={() => upd(i, { qty: c.qty + 1 })}><Plus className="h-3 w-3" /></Button>}
                    <span className="text-xs text-muted-foreground">{c.product.unit} ×</span>
                    {can("bills.edit") ? <Input type="number" className="h-8 w-20" value={c.price} onChange={(e) => upd(i, { price: Number(e.target.value) })} data-testid={`cart-price-${c.product.erp_code}`} /> : <span className="text-sm">{inr(c.price)}</span>}
                    <Input type="number" className="h-8 w-16" placeholder="disc" value={c.discount || ""} onChange={(e) => upd(i, { discount: Number(e.target.value) })} data-testid={`cart-discount-${c.product.erp_code}`} />
                    <span className="ml-auto text-sm font-bold">{inr(c.qty * c.price - (c.discount || 0))}</span>
                  </div>
                </div>);
            })}
          </div>
          <div className="border-t p-3 space-y-3">
            <div className="text-sm space-y-1">
              <div className="flex justify-between"><span>Subtotal</span><span>{inr(est.gross)}</span></div>
              <div className="flex justify-between items-center"><span>Bill discount ₹</span><Input data-testid="pos-bill-discount" type="number" className="h-7 w-24 text-right" value={billDisc || ""} onChange={(e) => setBillDisc(e.target.value)} /></div>
              <div className="flex justify-between text-muted-foreground"><span>GST (incl.)</span><span>{inr(est.tax)}</span></div>
              <div className="flex justify-between text-xl font-extrabold font-display"><span>Total</span><span data-testid="pos-grand-total">{inr(est.grand)}</span></div>
            </div>
            <div>
              <div className="text-xs font-semibold uppercase tracking-wider text-muted-foreground mb-1.5">Choose type of payment required</div>
              <div className="grid grid-cols-4 gap-1.5" data-testid="payment-type-selector">
                {methods.map((m) => <Button key={m.code} size="sm" variant={ptype === m.code ? "default" : "outline"} onClick={() => setPtype(m.code)} data-testid={`payment-type-${m.code}`}>{m.label}</Button>)}
                {methods.length > 1 && <Button size="sm" variant={ptype === "split" ? "default" : "outline"} onClick={() => setPtype("split")} data-testid="payment-type-split">Split</Button>}
              </div>
              {ptype === "cash" && <div className="flex items-center gap-2 mt-2"><Input data-testid="cash-tendered-input" type="number" placeholder={`Cash received (${est.grand})`} value={tendered} onChange={(e) => setTendered(e.target.value)} />
                {Number(tendered) > est.grand && <span className="text-sm font-semibold text-emerald-700 whitespace-nowrap">Change {inr(Number(tendered) - est.grand)}</span>}</div>}
              {ptype !== "cash" && ptype !== "split" && <Input className="mt-2" data-testid="payment-reference-input" placeholder="Transaction / UTR reference (optional)" value={ref} onChange={(e) => setRef(e.target.value)} />}
              {ptype === "split" && <div className="grid grid-cols-2 gap-2 mt-2">
                {methods.map((m) => <Input key={m.code} type="number" placeholder={m.label} value={split[m.code] || ""} onChange={(e) => setSplit({ ...split, [m.code]: e.target.value })} data-testid={`split-amount-${m.code}`} />)}
                <div className="col-span-2 text-xs">Balance due: <b>{inr(est.grand - Object.values(split).reduce((s, v) => s + Number(v || 0), 0))}</b></div></div>}
            </div>
            <div className="grid grid-cols-3 gap-2">
              <Button variant="outline" onClick={hold} disabled={!cart.length} data-testid="hold-bill-button"><Pause className="h-4 w-4 mr-1" />Hold</Button>
              <Button variant="outline" onClick={openHeld} data-testid="resume-bill-button"><PlayCircle className="h-4 w-4 mr-1" />Resume</Button>
              <Button variant="ghost" onClick={() => setCart([])} disabled={!cart.length} data-testid="clear-cart-button">Clear</Button>
            </div>
            <Button className="w-full h-14 text-lg font-bold" onClick={checkout} disabled={!cart.length || busy || !shift} data-testid="checkout-button">
              {busy ? "Processing…" : `Checkout ${inr(est.grand)}  ·  F8`}</Button>
          </div>
        </div>
      </div>
      <CameraScanner open={cam} onClose={() => setCam(false)} onCode={scan} />
      <PrintDialog open={!!printDoc} onClose={() => { setPrintDoc(null); searchRef.current?.focus(); }} doc={printDoc} />
      <Dialog open={heldOpen} onOpenChange={setHeldOpen}>
        <DialogContent data-testid="held-bills-dialog"><DialogHeader><DialogTitle>Held bills</DialogTitle></DialogHeader>
          {held.length ? held.map((h) => <div key={h.id} className="flex justify-between items-center border-t py-2 text-sm"><span>{h.customer?.name} · {h.cart.length} items · {dt(h.created_at)} · {h.by}</span>
            <Button size="sm" onClick={() => resume(h)} data-testid={`resume-held-${h.id}`}>Resume</Button></div>) : <p className="text-sm text-muted-foreground">No held bills</p>}
        </DialogContent>
      </Dialog>
      <Dialog open={!!shiftDlg} onOpenChange={(o) => !o && setShiftDlg(null)}>
        <DialogContent data-testid="shift-dialog"><DialogHeader><DialogTitle>{shiftDlg === "open" ? "Open cashier shift" : "Close shift & reconcile cash"}</DialogTitle></DialogHeader>
          {shiftDlg === "close" && closeSummary && <div className="text-sm space-y-1 rounded-lg bg-slate-50 p-3">
            <div>Bills: <b>{closeSummary.bills}</b></div>
            {Object.entries(closeSummary.collections).map(([k, v]) => <div key={k}>Collected · {k.toUpperCase()}: <b>{inr(v)}</b></div>)}
            {Object.entries(closeSummary.refunds).map(([k, v]) => <div key={k}>Refunds · {k.toUpperCase()}: <b>{inr(v)}</b></div>)}
            <div className="text-base">Expected cash in drawer: <b data-testid="expected-cash">{inr(closeSummary.expected_cash)}</b></div></div>}
          <Field label={shiftDlg === "open" ? "Opening cash ₹" : "Counted cash ₹"}><Input data-testid="shift-cash-input" type="number" value={cashInput} onChange={(e) => setCashInput(e.target.value)} /></Field>
          <DialogFooter><Button onClick={shiftAction} data-testid="shift-confirm-button">{shiftDlg === "open" ? "Open shift" : "Close shift"}</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
